import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Coins, Zap, MonitorPlay, Gift, Wallet, Shield, ArrowRight, Radar, TrendingUp } from "lucide-react";
import { Button } from "../components/ui/button";

const stat = (v, l) => ({ v, l });
const STATS = [stat("$1.2M+", "Paid to members"), stat("48K+", "Active earners"), stat("1,000", "Coins = $1"), stat("$5", "Min cashout")];

const FEATURES = [
  { icon: MonitorPlay, title: "Watch Ads", desc: "Earn coins for every ad you watch, from 15s spots to full trailers.", color: "text-cyan-neon" },
  { icon: Gift, title: "Complete Offers", desc: "Surveys, app installs & crypto tasks pay up to 8,000 coins each.", color: "text-[#FF007A]" },
  { icon: Wallet, title: "PayPal Cashout", desc: "Withdraw real money straight to your PayPal from just $5.", color: "text-lime-neon" },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen grid-bg">
      <header className="max-w-7xl mx-auto px-4 lg:px-8 py-5 flex items-center">
        <div className="flex items-center gap-2">
          <span className="relative flex items-center justify-center w-9 h-9 rounded-lg bg-cyan-400/10 border border-cyan-400/40">
            <Radar className="w-5 h-5 text-cyan-neon" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-cyan-400 radar-dot" />
          </span>
          <span className="font-display font-black text-lg tracking-tight">ADS<span className="text-cyan-neon">RUSH</span></span>
        </div>
        <Button onClick={() => navigate("/login")} data-testid="header-login-button" className="ml-auto btn-neon">
          Sign In
        </Button>
      </header>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 pt-10 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="eyebrow mb-4">/ WATCH · EARN · CASH OUT</p>
          <h1 className="font-display font-black uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
            Get paid to<br />
            <span className="text-cyan-neon">watch ads</span> &<br />
            <span className="text-lime-neon">crush offers</span>
          </h1>
          <p className="mt-6 text-slate-400 text-base leading-relaxed max-w-md">
            Turn your screen time into real cash. Rack up coins watching ads and completing offers, then withdraw instantly to PayPal.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={() => navigate("/login")} data-testid="hero-start-button" className="btn-neon h-12 px-7 text-base">
              Start Earning <ArrowRight className="w-5 h-5 ml-1" />
            </Button>
            <div className="flex items-center gap-2 px-4 h-12 rounded-md border border-slate-700 text-slate-300">
              <TrendingUp className="w-4 h-4 text-lime-neon" />
              <span className="text-sm">500 free coins on signup</span>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {STATS.map((s) => (
              <div key={s.l}>
                <p className="font-mono-num font-bold text-xl text-cyan-neon">{s.v}</p>
                <p className="text-xs text-slate-500 mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="relative floaty"
        >
          <div className="absolute -inset-6 bg-cyan-400/10 blur-3xl rounded-full" />
          <img
            src="https://images.unsplash.com/photo-1642791401714-a0d4e02d4bb6?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
            alt="Neon battlestation"
            className="relative rounded-2xl border border-cyan-500/20 shadow-2xl w-full object-cover aspect-[4/3]"
          />
          <div className="absolute -bottom-5 -left-5 glass rounded-xl px-4 py-3 flex items-center gap-3">
            <Coins className="w-6 h-6 text-amber-neon" />
            <div>
              <p className="font-mono-num font-bold text-lg leading-none">+2,450</p>
              <p className="text-xs text-slate-400 mt-1">coins today</p>
            </div>
          </div>
          <div className="absolute -top-5 -right-3 glass rounded-xl px-4 py-3 flex items-center gap-3">
            <Zap className="w-6 h-6 text-lime-neon" />
            <div>
              <p className="font-mono-num font-bold text-lg leading-none text-lime-neon">$24.50</p>
              <p className="text-xs text-slate-400 mt-1">cashed out</p>
            </div>
          </div>
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 pb-24 grid md:grid-cols-3 gap-5">
        {FEATURES.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="neon-card p-6"
          >
            <f.icon className={`w-8 h-8 ${f.color}`} />
            <h3 className="font-display font-bold text-xl mt-4">{f.title}</h3>
            <p className="text-slate-400 text-sm mt-2 leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </section>
    </div>
  );
}
